"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getAllTasks, AssuranceTask } from "@/services/assuranceActionsService";
import { toast } from "sonner";
import { AssuranceScheduler } from "../../../shared/services/AssuranceScheduler";
import { DuckLoadingScreen } from "@/components/DuckLoadingScreen";
import { db, FirebaseTimestamp } from "@/js/external-js/config/main_config";
import { useTenancy } from "@/contexts/TenancyContext";
import { SingleUserCombobox } from "@/components/ui/SingleUserCombobox";
import { useOnboarding } from "@/onboarding/OnboardingContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";

interface ScheduleConfig {
  startDate: string;
  weeklyCapacity: number;
  tasksPerWeek: number;
}

interface UIAssuranceTask {
    id: string;
    title: string;
    description: string;
    assignedTo: string;
    estimatedHours: number;
    priority: "high" | "medium" | "low";
    originalTask: AssuranceTask;
}

interface ScheduledTask {
  taskId: string;
  title: string;
  date: string;
  estimatedHours: number;
}

export default function ModalAssuranceTaskSchedulingExample() {
  const { userIdToName } = useTenancy();
  const { closeActive, setStatus, goBack } = useOnboarding();
  const [open, setOpen] = React.useState(true);
  const [mode, setMode] = React.useState<'simple' | 'advanced'>('simple');
  const [selected, setSelected] = React.useState<string[]>([]);
  const [scheduleConfig, setScheduleConfig] = React.useState<ScheduleConfig>({
    startDate: new Date().toISOString().split('T')[0],
    weeklyCapacity: 40,
    tasksPerWeek: 2
  });
  const [generatedSchedule, setGeneratedSchedule] = React.useState<ScheduledTask[]>([]);
  const [assuranceTasks, setAssuranceTasks] = React.useState<UIAssuranceTask[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  // Simple mode state
  const [simpleStartDate, setSimpleStartDate] = React.useState<Date | undefined>(new Date());
  const [simpleFrequency, setSimpleFrequency] = React.useState("weekly");
  const [simpleIntensity, setSimpleIntensity] = React.useState("medium"); // low, medium, high

  React.useEffect(() => {
      const fetchTasks = async () => {
          setLoading(true);
          try {
              const tasks = await getAllTasks();
              const mappedTasks: UIAssuranceTask[] = tasks.map(t => ({
                  id: t.assID || 'unknown',
                  title: t.checkDetail?.check || 'Untitled Task',
                  description: `Assigned to: ${t.checkDetail?.assignedTo || 'Unassigned'}`, // Fallback description
                  assignedTo: t.checkDetail?.assignedTo || '',
                  estimatedHours: 4, // Default as it's not in the interface
                  priority: 'medium', // Default as it's not in the interface
                  originalTask: t
              }));
              setAssuranceTasks(mappedTasks);
          } catch (e) {
              console.error("Failed to fetch tasks", e);
              toast.error("Failed to fetch assurance tasks");
          } finally {
              setLoading(false);
          }
      };
      fetchTasks();
  }, []);

  const toggleTask = (id: string) => {
    setSelected(prev => 
      prev.includes(id) 
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const updateTaskAssignee = (id: string, newAssignee: string) => {
      setAssuranceTasks(prev => prev.map(task => 
          task.id === id 
            ? { ...task, assignedTo: newAssignee } 
            : task
      ));
  };

  const updateScheduleConfig = (field: keyof ScheduleConfig, value: string | number) => {
    setScheduleConfig(prev => ({ ...prev, [field]: value }));
  };

  // Helper to format date as YYYY-MM-DD in local timezone (not UTC)
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to parse YYYY-MM-DD string as local date (not UTC)
  // This prevents timezone issues where "2025-01-15" parsed as UTC shows as Jan 14 in AU timezone
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const generateSchedule = () => {
    const configToUse = mode === 'simple' ? {
        startDate: simpleStartDate ? formatLocalDate(simpleStartDate) : formatLocalDate(new Date()),
        weeklyCapacity: simpleIntensity === 'low' ? 10 : simpleIntensity === 'medium' ? 20 : 40,
        tasksPerWeek: simpleIntensity === 'low' ? 1 : simpleIntensity === 'medium' ? 2 : 5
    } : scheduleConfig;

    const tasksToSchedule = mode === 'simple' ? assuranceTasks : selected.map(id => 
      assuranceTasks.find(t => t.id === id)
    ).filter(Boolean) as UIAssuranceTask[];

    if (!configToUse.startDate || tasksToSchedule.length === 0) return;

    const schedule: ScheduledTask[] = [];
    // Parse date string as local date (not UTC) to avoid off-by-one day issues
    const [year, month, day] = configToUse.startDate.split('-').map(Number);
    const startDate = new Date(year, month - 1, day);
    
    // Sort by priority (high first) then by estimated hours
    tasksToSchedule.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      return b.estimatedHours - a.estimatedHours;
    });

    let currentDate = new Date(startDate);
    let tasksThisPeriod = 0;
    let hoursThisPeriod = 0;

    // Simple scheduling algorithm
    for (const task of tasksToSchedule) {
      // Find next available slot
      // Fix infinite loop: Only skip to next week if we've reached task limit OR if we have already scheduled tasks 
      // and adding this one would exceed capacity. If a single task is larger than capacity, we schedule it in a fresh week.
      let loopGuard = 0;
      while ((tasksThisPeriod >= configToUse.tasksPerWeek || 
             (hoursThisPeriod > 0 && hoursThisPeriod + task.estimatedHours > configToUse.weeklyCapacity)) && loopGuard < 52) {
        // Move to next period
        if (simpleFrequency === 'weekly' || mode === 'advanced') {
             currentDate.setDate(currentDate.getDate() + 7);
        } else {
             currentDate.setMonth(currentDate.getMonth() + 1);
        }
        tasksThisPeriod = 0;
        hoursThisPeriod = 0;
        loopGuard++;
      }

      // Schedule task
      // Distribute tasks evenly across the period (weekdays only)
      const periodLength = (simpleFrequency === 'weekly' || mode === 'advanced') ? 5 : 20; // Use weekdays only
      const dayOffset = Math.floor((tasksThisPeriod * periodLength) / Math.max(configToUse.tasksPerWeek, 1));
      
      // Convert weekday offset to actual calendar days (skipping weekends)
      const taskDate = new Date(currentDate);
      let weekdaysAdded = 0;
      while (weekdaysAdded < dayOffset) {
        taskDate.setDate(taskDate.getDate() + 1);
        const dayOfWeek = taskDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Skip Sunday (0) and Saturday (6)
          weekdaysAdded++;
        }
      }
      
      // Ensure the final date is also a weekday
      while (taskDate.getDay() === 0 || taskDate.getDay() === 6) {
        taskDate.setDate(taskDate.getDate() + 1);
      }

      schedule.push({
        taskId: task.id,
        title: task.title,
        date: formatLocalDate(taskDate),
        estimatedHours: task.estimatedHours
      });

      tasksThisPeriod++;
      hoursThisPeriod += task.estimatedHours;
    }

    setGeneratedSchedule(schedule);
  };

  const handleSaveSchedule = async () => {
      if (generatedSchedule.length === 0) return;
      setSaving(true);
      try {
          const companyID = (window as any).companyID;
          if (!companyID) throw new Error("No company ID found");

          // Prepare schedule for scheduler
          const scheduleItems = generatedSchedule.map(s => {
              const task = assuranceTasks.find(t => t.id === s.taskId);
              return {
                  taskId: s.taskId,
                  date: s.date,
                  assignedTo: task?.assignedTo
              };
          });

          await AssuranceScheduler.applyScheduleToFirestore(
              scheduleItems,
              companyID,
              db,
              (date) => FirebaseTimestamp.fromDate(date)
          );

          toast.success("Schedule saved successfully!");
          setStatus("assurance_task_scheduling", "done");
          setOpen(false);
          closeActive();
      } catch (error) {
          console.error("Failed to save schedule", error);
          toast.error("Failed to save schedule. Please try again.");
      } finally {
          setSaving(false);
      }
  };

  const selectedCount = selected.length;
  const totalHours = selected.reduce((sum, id) => {
    const task = assuranceTasks.find(t => t.id === id);
    return sum + (task?.estimatedHours || 0);
  }, 0);
  
  const canGenerateAdvanced = selectedCount > 0 && scheduleConfig.startDate && scheduleConfig.tasksPerWeek > 0 && scheduleConfig.weeklyCapacity > 0;

  // Trigger generation in simple mode when inputs change (and tasks are loaded)
  React.useEffect(() => {
      if (mode === 'simple' && assuranceTasks.length > 0) {
          generateSchedule();
      }
  }, [simpleStartDate, simpleFrequency, simpleIntensity, mode, assuranceTasks]);

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Assurance Task Scheduling</Button>
      {/* Duck Loading Screen is now inside the modal */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) closeActive(); }}>
        <DialogContent className="max-w-7xl! w-7xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={() => { setOpen(false); closeActive(); }}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Duck Loading Screen Overlay */}
            {saving && (
              <div className="absolute inset-0 z-50 rounded-xl overflow-hidden">
                <DuckLoadingScreen
                  variant="overlay"
                  title="Scheduling Tasks"
                  text="We're setting up your assurance program schedule."
                  duckFacts={[
                    "Quack! Scheduling your compliance tasks...",
                    "Distributing workload across weeks...",
                    "Optimising task assignments...",
                    "Almost there! Finalising your schedule..."
                  ]}
                />
              </div>
            )}
            
            <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent flex justify-between items-start">
              <div>
                <h2 className="text-3xl font-bold text-foreground">Set Up Your Assurance Program</h2>
                <p className="mt-2 text-muted-foreground">
                    Select a start date and your task frequency, and we will create your assurance schedule.
                </p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">Loading tasks...</div>
                ) : mode === 'simple' ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
                        {/* Simple Controls */}
                        <Card className="bg-white/5 border-white/20 lg:col-span-1 h-fit">
                            <CardHeader>
                                <CardTitle>Schedule Settings</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label>When do you want to begin?</Label>
                                    <Calendar
                                        mode="single"
                                        selected={simpleStartDate}
                                        onSelect={setSimpleStartDate}
                                        className="rounded-md border border-white/10 shadow-sm dark:shadow-none dark:bg-black/20 w-full flex justify-center"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>How often should these happen?</Label>
                                    <Select value={simpleFrequency} onValueChange={setSimpleFrequency}>
                                        <SelectTrigger className="bg-white/10 border-white/20">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="weekly">Every Week</SelectItem>
                                            <SelectItem value="monthly">Every Month</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>How many do you want to do each time?</Label>
                                    <Select value={simpleIntensity} onValueChange={setSimpleIntensity}>
                                        <SelectTrigger className="bg-white/10 border-white/20">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="low">1 task</SelectItem>
                                            <SelectItem value="medium">2 tasks</SelectItem>
                                            <SelectItem value="high">5 tasks</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Preview */}
                        <Card className="bg-white/5 border-white/20 lg:col-span-2 h-full flex flex-col max-h-[calc(100vh-42rem)]">
                            <CardHeader>
                                <CardTitle>Your Plan to Compliance</CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 overflow-y-auto space-y-3 min-h-0">
                                {generatedSchedule.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        Configure settings to generate a preview
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Group by month for better preview */}
                                        {Object.entries(generatedSchedule.reduce((acc, task) => {
                                            const month = parseLocalDate(task.date).toLocaleString('default', { month: 'long', year: 'numeric' });
                                            if (!acc[month]) acc[month] = [];
                                            acc[month].push(task);
                                            return acc;
                                        }, {} as Record<string, ScheduledTask[]>)).map(([month, tasks]) => (
                                            <div key={month} className="space-y-2">
                                                <h3 className="font-semibold text-primary/80 sticky top-0 backdrop-blur-sm py-2 z-10 rounded-sm">{month}</h3>
                                                <div className="grid gap-2">
                                                    {tasks.map((scheduled, index) => {
                                                        const task = assuranceTasks.find(t => t.id === scheduled.taskId);
                                                        return (
                                                            <div key={index} className="flex items-center justify-between p-3 rounded-lg border border-white/10 bg-white/5 shadow-sm hover:bg-white/10 transition-colors">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="flex flex-col items-center justify-center min-w-12! min-h-12! w-12 h-12 rounded-md bg-primary/10 text-primary border border-primary/20">
                                                                        <span className="text-xs font-bold uppercase">{parseLocalDate(scheduled.date).toLocaleString('default', { month: 'short' })}</span>
                                                                        <span className="text-lg font-bold leading-none">{parseLocalDate(scheduled.date).getDate()}</span>
                                                                    </div>
                                                                    <div>
                                                                        <div className="font-medium text-foreground">{scheduled.title}</div>
                                                                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                                                                            <span>Assigned to:</span>
                                                                            {task && (
                                                                                <div className="w-fit inline-block">
                                                                                    <SingleUserCombobox 
                                                                                        selected={task.assignedTo} 
                                                                                        setSelected={(uid) => updateTaskAssignee(task.id, uid)} 
                                                                                        className="h-auto p-0 border-none shadow-none! text-xs text-muted-foreground border-0 bg-transparent! hover:bg-transparent! justify-start w-auto" 
                                                                                        hideChevron
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {/* <Badge variant="secondary" className="whitespace-nowrap">{scheduled.estimatedHours}h</Badge> */}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full overflow-hidden">
                        {/* Task Selection */}
                        <Card className="bg-white/5 border-white/20 lg:col-span-1 flex flex-col">
                        <CardHeader>
                            <CardTitle className="text-lg">Select Tasks</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 flex-1 overflow-y-auto min-h-0">
                            {assuranceTasks.map(task => (
                            <div key={task.id} className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-white/5">
                                <Checkbox
                                checked={selected.includes(task.id)}
                                onCheckedChange={() => toggleTask(task.id)}
                                />
                                <div className="flex-1 min-w-0">
                                <div className="font-medium text-foreground">{task.title}</div>
                                    <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                                    <span>Assigned to:</span>
                                    <SingleUserCombobox 
                                        selected={task.assignedTo} 
                                        setSelected={(uid) => updateTaskAssignee(task.id, uid)} 
                                        minimal
                                        buttonClassName="text-xs text-muted-foreground underline decoration-dotted underline-offset-2"
                                    />
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <Badge variant="secondary" className="text-xs">{task.estimatedHours}h</Badge>
                                    <Badge variant={task.priority === 'high' ? 'destructive' : task.priority === 'medium' ? 'default' : 'secondary'} className="text-xs">
                                    {task.priority}
                                    </Badge>
                                </div>
                                </div>
                            </div>
                            ))}
                        </CardContent>
                        </Card>

                        {/* Schedule Configuration */}
                        <Card className="bg-white/5 border-white/20 lg:col-span-1 h-fit">
                        <CardHeader>
                            <CardTitle className="text-lg">Schedule Settings</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
                            <Input
                                type="date"
                                value={scheduleConfig.startDate}
                                onChange={(e) => updateScheduleConfig('startDate', e.target.value)}
                                className="bg-white/10 border-white/20"
                            />
                            </div>
                            
                            <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Weekly Capacity (hours)</label>
                            <Input
                                type="number"
                                min="1"
                                max="80"
                                value={scheduleConfig.weeklyCapacity}
                                onChange={(e) => updateScheduleConfig('weeklyCapacity', parseInt(e.target.value) || 0)}
                                className="bg-white/10 border-white/20"
                            />
                            <div className="text-xs text-muted-foreground mt-1">
                                Maximum hours available per week for assurance tasks
                            </div>
                            </div>

                            <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Tasks Per Week</label>
                            <Input
                                type="number"
                                min="1"
                                max="10"
                                value={scheduleConfig.tasksPerWeek}
                                onChange={(e) => updateScheduleConfig('tasksPerWeek', parseInt(e.target.value) || 0)}
                                className="bg-white/10 border-white/20"
                            />
                            <div className="text-xs text-muted-foreground mt-1">
                                Maximum number of tasks to schedule per week
                            </div>
                            </div>

                            <Button 
                            onClick={generateSchedule} 
                            disabled={!canGenerateAdvanced}
                            className="w-full"
                            >
                            Generate Schedule
                            </Button>
                        </CardContent>
                        </Card>

                        {/* Generated Schedule */}
                        <Card className="bg-white/5 border-white/20 lg:col-span-1 flex flex-col">
                        <CardHeader>
                            <CardTitle className="text-lg">Generated Schedule</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 flex-1 overflow-y-auto min-h-0">
                            {generatedSchedule.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                Select tasks and configure settings to generate schedule
                            </div>
                            ) : (
                            generatedSchedule.map((scheduled, index) => {
                                const task = assuranceTasks.find(t => t.id === scheduled.taskId);
                                return (
                                <div key={index} className="p-3 rounded-lg border border-white/10 bg-white/5">
                                    <div className="font-medium text-foreground">{scheduled.title}</div>
                                    <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                                        <span>Assigned to:</span>
                                        {task && (
                                            <SingleUserCombobox 
                                                selected={task.assignedTo} 
                                                setSelected={(uid) => updateTaskAssignee(task.id, uid)} 
                                                minimal
                                                buttonClassName="text-xs text-muted-foreground underline decoration-dotted underline-offset-2"
                                            />
                                        )}
                                    </div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                    {parseLocalDate(scheduled.date).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        year: 'numeric', 
                                        month: 'short', 
                                        day: 'numeric' 
                                    })}
                                    </div>
                                    <div className="flex items-center gap-2 mt-2">
                                    <Badge variant="secondary" className="text-xs">{scheduled.estimatedHours}h</Badge>
                                    {task && (
                                        <Badge variant={task.priority === 'high' ? 'destructive' : task.priority === 'medium' ? 'default' : 'secondary'} className="text-xs">
                                        {task.priority}
                                        </Badge>
                                    )}
                                    </div>
                                </div>
                                );
                            })
                            )}
                        </CardContent>
                        </Card>
                    </div>
                )}
            </div>

            <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {generatedSchedule.length > 0 ? (
                  <span className="text-green-600">
                    Plan made: {generatedSchedule.length} tasks across {Math.ceil((generatedSchedule.length || 1) / (mode === 'simple' ? (simpleIntensity === 'low' ? 1 : simpleIntensity === 'medium' ? 2 : 5) : scheduleConfig.tasksPerWeek))} {mode === 'simple' && simpleFrequency === 'monthly' ? 'months' : 'weeks'}
                  </span>
                ) : (
                  <span>{mode === 'simple' ? 'Configure settings to preview schedule' : 'Select tasks and generate schedule to continue'}</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setOpen(false); goBack(); }}>Back</Button>
                <Button disabled={generatedSchedule.length === 0} onClick={handleSaveSchedule}>
                  Save this Plan
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
